import { useState } from 'react';
import { api, ApiFailure, type Report } from '../api';
import { Notice } from './Panels';

/**
 * Automated client reporting — compose branded reports and email them to clients.
 * Combines the analysis findings with custom branding into a polished deliverable.
 */
export function ClientReports({ report }: { report: Report }) {
  const [mode, setMode] = useState<'compose' | 'send'>('compose');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [period, setPeriod] = useState(new Date().toLocaleString('default', { month: 'long', year: 'numeric' }));
  const [accentColor, setAccentColor] = useState('#2563eb');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<{ sent: boolean; detail: string } | null>(null);

  async function compose(): Promise<void> {
    setBusy(true);
    setFailure(null);
    setPreviewHtml(null);
    try {
      const r = await api.composeClientReport(report.id, {
        clientName,
        companyName: companyName || undefined,
        period,
        accentColor,
      });
      setPreviewHtml(r.html);
    } catch (e) { setFailure(asFailure(e)); }
    finally { setBusy(false); }
  }

  async function send(): Promise<void> {
    if (clientEmail.trim() === '') return;
    setBusy(true);
    setFailure(null);
    setSendResult(null);
    try {
      const r = await api.sendClientReport(report.id, {
        clientName,
        clientEmail,
        companyName: companyName || undefined,
        period,
        accentColor,
      });
      setSendResult({ sent: r.sent, detail: r.detail });
    } catch (e) { setFailure(asFailure(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="page">
      <header className="intro">
        <h2>Client Reports</h2>
        <p>
          Compose a branded report from this analysis and send it to your client.
          The report includes an executive summary, key metrics, findings, and recommendations —
          all styled with your branding.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <div className="segmented" style={{ alignSelf: 'flex-start' }}>
        <button aria-pressed={mode === 'compose'} onClick={() => setMode('compose')}>Preview</button>
        <button aria-pressed={mode === 'send'} onClick={() => setMode('send')}>Send to client</button>
      </div>

      <section className="card">
        <header><h2>Report settings</h2></header>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12 }}>
          <label className="field">
            Client name
            <input type="text" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Acme Corp" />
          </label>
          <label className="field">
            Your company name
            <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Your Agency" />
          </label>
          <label className="field">
            Reporting period
            <input type="text" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="January 2025" />
          </label>
          <label className="field">
            Accent colour
            <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} />
          </label>
        </div>

        {mode === 'send' && (
          <label className="field" style={{ marginTop: 14 }}>
            Client email
            <input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@company.com" />
          </label>
        )}

        <div className="row" style={{ marginTop: 16 }}>
          {mode === 'compose' && (
            <button className="primary" onClick={compose} disabled={busy || clientName.trim() === ''}>
              {busy ? <span className="spinner" /> : 'Generate preview'}
            </button>
          )}
          {mode === 'send' && (
            <button className="primary" onClick={send} disabled={busy || clientName.trim() === '' || clientEmail.trim() === ''}>
              {busy ? <span className="spinner" /> : 'Send report'}
            </button>
          )}
        </div>

        {sendResult !== null && (
          <div style={{ marginTop: 12 }}>
            <Notice
              tone={sendResult.sent ? 'good' : 'error'}
              message={sendResult.sent ? 'Report sent successfully.' : 'Report could not be sent.'}
              remedy={sendResult.detail}
            />
          </div>
        )}
      </section>

      {previewHtml !== null && (
        <section className="card" style={{ marginTop: 16 }}>
          <header>
            <h2>Report preview</h2>
            <button className="ghost tiny" onClick={() => {
              const blob = new Blob([previewHtml], { type: 'text/html' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `report-${period.toLowerCase().replace(/\s+/g, '-')}.html`; a.click();
              URL.revokeObjectURL(url);
            }}>Download HTML</button>
          </header>
          <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <iframe
              srcDoc={previewHtml}
              title="Report preview"
              style={{ width: '100%', height: 600, border: 'none' }}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}
